import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';

/** A sheet opened recently on this browser (W44) */
export interface RecentSheet {
  uuid: string;
  number: string;
  title: string | null;
  /** ISO time it was opened */
  at: string;
}

const KEY = 'sitenotes.drawings.recent';
export const RECENT_SHEETS_MAX = 10;

/**
 * The last sheets a user opened, per project, newest first (W44). Kept in localStorage under the signed-in account's
 * id, so another account on the same browser never sees them (logout only clears NgRx).
 */
@Injectable({ providedIn: 'root' })
export class RecentSheetsService {
  private auth = inject(AuthService);

  list(projectId: string): RecentSheet[] {
    return this.read()[projectId] ?? [];
  }

  record(projectId: string, sheet: { uuid: string; number?: string | null; title?: string | null }): void {
    if (!projectId || !sheet?.uuid || !this.accountKey()) {
      return;
    }
    const all = this.read();
    const entry: RecentSheet = { uuid: sheet.uuid, number: sheet.number ?? '', title: sheet.title ?? null, at: new Date().toISOString() };
    all[projectId] = [entry, ...(all[projectId] ?? []).filter(s => s.uuid !== sheet.uuid)].slice(0, RECENT_SHEETS_MAX);
    this.write(all);
  }

  /**
   * Against the project's full sheet list (I2): drops sheets it no longer has (deleted, or out of this account's
   * reach) and refreshes renamed or renumbered ones. Returns the list.
   */
  reconcile(projectId: string, sheets: { uuid: string; number?: string | null; title?: string | null }[], drop = true): RecentSheet[] {
    const all = this.read();
    const current = all[projectId];
    if (!current?.length) {
      return [];
    }
    const byUuid = new Map(sheets.map(s => [s.uuid, s]));
    // `drop` false (only a page of the list, W57): renames only; a sheet not on the page may still be there
    const next = current.filter(r => !drop || byUuid.has(r.uuid)).map(r => {
      const s = byUuid.get(r.uuid);
      return s ? { ...r, number: s.number ?? r.number, title: s.title ?? null } : r;
    });
    if (JSON.stringify(next) !== JSON.stringify(current)) {
      all[projectId] = next;
      this.write(all);
    }
    return next;
  }

  /** A sheet deleted or not visible any more */
  forget(projectId: string, uuid: string): void {
    const all = this.read();
    if (all[projectId]) {
      all[projectId] = all[projectId].filter(s => s.uuid !== uuid);
      this.write(all);
    }
  }

  private accountKey(): string | null {
    const id = this.auth.getCurrentUser()?.id;
    return id === null || id === undefined ? null : `${KEY}.${id}`;
  }

  private read(): Record<string, RecentSheet[]> {
    const key = this.accountKey();
    if (!key) {
      return {};
    }
    try {
      const parsed = JSON.parse(localStorage.getItem(key) ?? '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private write(all: Record<string, RecentSheet[]>): void {
    const key = this.accountKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(all));
    } catch {
      // Storage full or blocked: recent sheets are a convenience, never an error
    }
  }
}
